import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import SubHeader from "../../custom/sub-header/sub-header";
import PaymentService from "../../../service/payment-service";
import BaiyeReport from "../../products/bazi-baiye/baiye-report/baiye-report";
import FeihongReport from "../../products/match-feihong/feihong-report/feihong-report";
import JingyueReport from "../../products/ziwei-jingyue/jingyue-report/jingyue-report";
import "./purchase-result-page-style.scss";
import videoUrl from "../../../utils/video-url";

//付款結果
//
//  綠界付完款會把瀏覽器導回這裡（ClientBackURL）。
//  但「瀏覽器被導回來」不等於「付款成功」—— 真正的依據是綠界的
//  server-to-server 通知（ReturnURL），那支才會改訂單狀態。
//  所以這頁一律回頭問後端訂單現在是什麼狀態，不看網址參數。
//
//  兩者是併行的：使用者可能比通知早幾秒回到這頁，看到的還是 created。
//  因此要輪詢一小段時間，而不是查一次就宣告失敗。
//
//  —— 查詢走公開端點（P-9）——
//  報告類商品不需要登入就能買（見後端 P-4b），所以走到這一頁的人
//  有可能從頭到尾沒有帳號。原本這裡用的是要登入的 P-7，
//  那會讓訪客付完錢看到一個查不到任何東西的頁面。
//  P-9 只回「付了沒」這種程度的資訊 —— orderID 會出現在網址上，
//  等於公開資訊，不能拿它換到發票統編與問診資料。
const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 20000;

//—— 報告產出時的掩護影片 ——
//
//  付完款之後 AI 要跑好幾分鐘。那段時間給一張靜止的「購買成功」卡片，
//  等於請人盯著一個不會動的畫面 —— 會以為卡住了。
//  所以接回白夜的最後一支影片（他低頭批命）：畫面上他正在做的事，
//  就是後端正在做的事，等待本身變成劇情的一部分。
//
//  一支產品一支：白夜低頭批命、鏡月蒙著眼看星盤。
//  掛在下面的 REPORT_PRODUCTS 裡，不在這裡選 —— 選哪一支要看訂單。
//  網址走共用的 utils/video-url（它會帶上版本參數，理由寫在那一支裡）。

//  影片開頭有一小段鏡頭還沒落定，跳過（數值同劇情頁的 INTRO_SKIP_S）
const VIDEO_SKIP_S = 0.3;

//—— 疊在掩護影片上的三句對話框 ——
//
//  做成資料表而不是寫死三段 JSX：位置、寬度、字級比例都是「要對著影片的
//  構圖調」的東西，改一個數字就好（作法與數值的量級都同故事頁的 bubbles）。
//
//  text 裡的 \n 是刻意的斷行 —— 框是縱向橢圓，一行放幾個字要由人決定。
//  交給瀏覽器自動折的話，它會沿著橢圓最寬處折成一條窄長的字柱
//  （見 scss 的 white-space: pre）。
//
//  ratio = 字級佔框寬的幾成。字多的框要調小 —— 同樣一顆橢圓，
//  放三個字和放三行能用的字級差一倍。參考點（量自故事頁現有的框）：
//    3 字一行     0.24
//    7 字兩行     0.205
//    14 字三行    0.168
//
//  from: "name" 代表值從外面帶進來（使用者的暱稱）。
const BAIYE_BUBBLES = [
  { from: "name", left: "22%", top: "16%", width: "30%" },
  {
    text: "我看見\n你的命運\n勒！",
    left: "40%",
    top: "22%",
    width: "36%",
    ratio: 0.168,
  },
  { text: "原來\n如此！", left: "26%", top: "18%", width: "30%", ratio: 0.2 },
];

//—— 鏡月那三句 ——
//
//  位置是看著 jingyue-loading.mp4 第 4 秒那一格擺的，不是照抄白夜的：
//  她是大特寫，臉佔住畫面中段（瀏海到下巴約 y 10%～55%），
//  眼罩在 y 25%～38%，右上角有一彎月、右下是衣袍上的星系 ——
//  框壓在那幾個地方會蓋掉畫面的主體。兩側的頭髮是整格最暗也最空的地方，
//  白色的框壓上去最讀得出來，所以三顆都靠邊走。
//
//  語氣也不是白夜那套。他是「我看見你的命運勒！」那種挑釁的得意，
//  她是陪著你看 —— 主詞給星，不給自己。
const JINGYUE_BUBBLES = [
  { from: "name", left: "6%", top: "10%", width: "30%" },
  {
    text: "你的星\n正在\n說話",
    left: "64%",
    top: "14%",
    width: "32%",
    ratio: 0.185,
  },
  { text: "原來\n是這樣", left: "8%", top: "12%", width: "30%", ratio: 0.2 },
];

//—— 三句的節奏 ——
//  一句停 HOLD，然後空 GAP 再換下一句。
//
//  中間一定要留空白：一句接一句不留縫的話，三句會像連珠炮，
//  而他是「語帶三分餘裕」的那種人 —— 說完一句停一下，才像在看著你等反應。
//  空白也讓影片自己演一段，不會整整九十秒都被框蓋著。
//
//  一輪 (3.6 + 1.4) × 3 = 15 秒，報告跑九十秒 → 大約重播六輪。
const BUBBLE_HOLD_MS = 3600;
const BUBBLE_GAP_MS = 1400;

//—— 緋紅那三句 ——
//
//  ⚠️ **這三顆的位置是對著舊影片量的，還沒對新的重量過。**
//  2026-10-30 換成專屬的 feihong-loading.mp4（在那之前借用開場的
//  feihong-5.mp4）。底下那串座標是量 feihong-5 的五格抓出來的 ——
//  新影片的構圖不一樣（她低頭、雙手捧著光，臉在畫面偏上），
//  所以要在真機上看過一次再決定要不要重擺。
//
//  —— 位置：三顆都在肩膀的高度，不碰臉（Kevin 2026-10-27）——
//
//  量影片的五格（0.5／2／4／6／8 秒，420×900、object-fit: cover）：
//    她的臉       x 48～70%   y 8～28%
//    肩膀那一帶   y 28～42%
//  舊值把框放在 y 15～20%，正好是臉的高度 —— 第二顆（left 42%）更是
//  直接蓋住整張臉。
//
//  新的擺法：兩顆靠左肩外側、一顆靠右肩外側，top 都落在 30% 之後
//  （臉的下緣是 28%）。左右各留一點邊，不要貼到畫面外。
//
//  ⚠️ 這支影片的構圖會動（她會轉身、紅線會掃過整個畫面），
//  所以三顆不是「絕對不碰到她」，是「絕對不碰到臉」——
//  壓在肩上的衣料是可以的，那本來就是暗的。
//
//  語氣是她的：不是白夜那種「我看見你的命運勒！」的得意，
//  也不是鏡月的空靈 —— 她是看著那根線，帶著點玩味慢慢說。
const FEIHONG_BUBBLES = [
  //左肩外側
  { from: "name", left: "4%", top: "30%", width: "30%" },
  //右肩外側。三行的框最高，top 給一樣的 30%，底部落在 57% 還在畫面內
  {
    text: "這根線\n纏得\n真有意思",
    left: "62%",
    top: "30%",
    width: "32%",
    ratio: 0.175,
  },
  //左肩外側，比第一顆再低一點，兩顆不要完全重疊在同一個位置
  { text: "原來\n是這樣啊", left: "5%", top: "34%", width: "31%", ratio: 0.195 },
];

//暱稱那顆框的字級比例。名字長度是使用者決定的（兩到五個字都有），
//  用固定值的話短名字會小得可憐、長名字會爆框。
const nameRatio = (name) => {
  const n = String(name || "").length;
  if (n <= 2) return 0.26;
  if (n === 3) return 0.24;
  if (n === 4) return 0.195;
  return 0.16;
};

//把「框寬佔畫面的百分比」換算成字級。
//  同故事頁的作法：框改大小時字自動跟著，不必兩個數字一起調。
const bubbleFontSize = (width, ratio) => `${parseFloat(width) * ratio}cqw`;

//—— 哪些商品買完要等 AI 產報告，以及那一支長什麼樣 ——
//
//  用名稱判斷而不是 productID —— order-status 這支公開端點只回得出
//  productName（理由見後端 P-9）。
//
//  做成一張表而不是一串 if：之後緋紅、伏爻子的報告上架時，
//  這裡多一列就接上了，渲染那一段一行都不用動。
//  match 對的是 productName 裡的角色名（後端建單時組進去的）。
const REPORT_PRODUCTS = [
  {
    match: "白夜",
    video: "baiye-loading.mp4",
    bubbles: BAIYE_BUBBLES,
    Report: BaiyeReport,
    //  白夜的語氣：從容、看透一切、說得準卻不替你緩頰。
    //  不要寫成「請稍候，系統處理中」那種介面口吻 ——
    //  前面十七分鐘的劇情都是他在跟你說話，到這裡換成系統公告，
    //  人會立刻意識到自己在看一個網站，而不是在聽一個人講話。
    title: "白夜正在批你的命",
    desc: ["我落筆慢，你急不得。", "寫成了自會攤在你眼前，另謄一份，送去你留的信箱。"],
    waiting: "正在翻你的命盤…",
    working: "白夜落筆了…",
  },
  {
    match: "鏡月",
    video: "jingyue-loading.mp4",
    bubbles: JINGYUE_BUBBLES,
    Report: JingyueReport,
    //  鏡月的語氣：溫婉、空靈、超脫，像聲音從星空傳來。
    //  ⚠️ 她算的是**紫微斗數**不是八字 —— 不要出現命盤、八字、批命那些詞，
    //  她看的是星、是盤上的十二個格子。
    //  「你急不得」那種帶著教訓的口吻也不是她 —— 她不催人，也不被催。
    title: "鏡月正在為你看星",
    desc: ["星走得慢，我也不趕它。", "看完了會攤在你眼前，另抄一份，送去你留的信箱。"],
    waiting: "星圖還在轉…",
    working: "她看見了…",
  },
  {
    match: "緋紅",
    video: "feihong-loading.mp4",
    bubbles: FEIHONG_BUBBLES,
    Report: FeihongReport,
    //  緋紅的語氣：妖媚、慵懶，帶點哀怨與通透，語調輕挑。
    //  ⚠️ 她算的是**兩個人的八字**（單身線是一個人的，但問的都是感情）——
    //  不要寫成白夜那種「批命」的口吻，她看的是線、是纏在誰身上。
    //  也不要寫成催促或安撫，她不急，她在玩那根線。
    title: "緋紅正在替你牽線",
    desc: ["線纏了這些年，本妖得一根一根挑開。", "挑完了攤給你看，另謄一份，送去你留的信箱。"],
    waiting: "指尖的紅線還在動…",
    working: "她挑開了…",
  },
];

const reportProductOf = (order) =>
  REPORT_PRODUCTS.find((p) =>
    String(order?.productName || "").includes(p.match),
  ) || null;

const isReportProduct = (order) => Boolean(reportProductOf(order));

//報告還沒好的時候多久問一次。
//  比訂單那個（1.5 秒）慢：訂單是「馬上要有結果」的等待，
//  報告要跑好幾分鐘，問那麼勤只是白打。
const REPORT_POLL_MS = 4000;

const PurchaseResultPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orderID = params.get("orderID");

  const [order, setOrder] = useState(null);
  //報告：pending / processing / done / failed，null 代表還沒開始（或不是報告類商品）
  const [reportStatus, setReportStatus] = useState(null);
  //這張單評價過了沒（收尾頁用）
  const [reviewed, setReviewed] = useState(false);
  //掩護影片上的節拍。偶數拍在講話、奇數拍留白（見 BUBBLE_HOLD_MS）
  const [step, setStep] = useState(0);
  const [report, setReport] = useState(null);
  //  —— 「還沒問到」跟「問到了、還沒好」是兩回事 ——
  //  report 是 null 時這兩種情況長得一樣，而它們該顯示的東西相反：
  //    還沒問到   → 什麼都還不知道，先給一個中性的等待
  //    問到了沒好 → 掩護影片（那是設計好的等待體驗）
  //  分不開的話，從收藏庫或信箱連結進來（報告早就產好了）的人會先看到
  //  一兩秒的「白夜正在批你的命」才跳出報告 —— 他等的是一本已經寫好的書。
  const [reportKnown, setReportKnown] = useState(false);
  const reportTimerRef = useRef(null);
  //waiting / paid / pending / failed / timeout / notfound
  const [state, setState] = useState("waiting");
  const timerRef = useRef(null);

  const stop = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const check = useCallback(async () => {
    try {
      const res = await PaymentService.orderStatus(orderID);
      const found = res.data?.data;
      if (!found) return null;
      setOrder(found);
      return found.status;
    } catch (e) {
      return null;
    }
  }, [orderID]);

  //—— 等報告 ——
  //
  //  訂單變成 paid 之後才開始。在那之前沒有 reportID，問了也只會拿到 null。
  //
  //  這一支跟上面那支訂單輪詢刻意分開，因為兩者的終點不同：
  //  訂單幾秒內就有結果（綠界的通知很快），報告要跑好幾分鐘。
  //  併成一支的話，間隔要遷就慢的那一邊，付款結果就會慢好幾秒才出現。
  //
  //  —— 沒有逾時 ——
  //  報告失敗時就讓它一直停在掩護影片上（這是設計上的決定）。
  //  使用者真正的出口是信箱裡那條連結 —— 產好了會寄，沒產好本來就不會寄。
  useEffect(() => {
    if (state !== "paid" || !orderID) return undefined;

    const tick = async () => {
      try {
        const res = await PaymentService.orderReport(orderID);
        const data = res.data?.data;
        if (!data) return;

        setReportStatus(data.reportStatus);
        setReviewed(Boolean(data.reviewed));
        if (data.reportStatus === "done" && data.content) {
          setReport(data.content);
          clearInterval(reportTimerRef.current);
        }
      } catch (e) {
        //查失敗不中斷：可能只是網路斷一下，下一輪就回來了
      } finally {
        //  成功或失敗都算「問過了」。失敗時退回舊行為（顯示掩護影片）——
        //  那是設計好的等待畫面，比卡在一個空白的轉圈好；
        //  下一輪問到真的狀態時自然會修正。
        setReportKnown(true);
      }
    };

    tick();
    reportTimerRef.current = setInterval(tick, REPORT_POLL_MS);
    return () => clearInterval(reportTimerRef.current);
  }, [state, orderID]);

  useEffect(() => {
    if (!orderID) {
      setState("notfound");
      return;
    }

    const deadline = Date.now() + POLL_TIMEOUT_MS;

    const tick = async () => {
      const status = await check();

      if (status === "paid") {
        setState("paid");
        stop();
        return;
      }

      //非即時付款（ATM / 超商）取號完成 —— 這也是終態，
      //不要一直等下去：真正的繳費可能是幾天後的事
      if (status === "pending") {
        setState("pending");
        stop();
        return;
      }

      if (status === "failed" || status === "cancelled") {
        setState("failed");
        stop();
        return;
      }

      //逾時仍未收到通知：不要說「失敗」——錢可能已經扣了，
      //只是通知還沒到。改為請使用者稍後查看訂單紀錄。
      if (Date.now() > deadline) {
        setState("timeout");
        stop();
      }
    };

    tick();
    timerRef.current = setInterval(tick, POLL_INTERVAL_MS);
    return stop;
  }, [orderID, check]);

  //掩護影片佔滿整個畫面的那段不掛頂部列：
  //  一條寫著「付款結果」的深色橫條壓在他臉上面，整幕的沉浸感就沒了。
  //  報告出來之後要掛回來 —— 那時是在讀一份文件，需要知道自己在哪、怎麼回去。
  //這一單是哪一支報告（白夜／鏡月）。不是報告類商品時是 null。
  //  掩護影片、那三句對話框、要掛哪一支報告元件都從這裡拿。
  const rp = reportProductOf(order);
  const bubbleCount = rp?.bubbles.length || 1;

  //  reportKnown 是必要的 —— 少了它，第一次輪詢回來之前就會先閃一次掩護影片。
  const covering =
    state === "paid" && isReportProduct(order) && reportKnown && !report;
  //  已付款、是報告類商品，但還沒問到報告的狀態
  const checking = state === "paid" && isReportProduct(order) && !reportKnown;
  //報告本身是滿版的一本書（fixed 定位），上面再壓一條「付款結果」很突兀
  const reading = state === "paid" && isReportProduct(order) && Boolean(report);

  //—— 三句對話框輪流出現 ——
  //
  //  step 是「第幾拍」，偶數拍顯示一句、奇數拍留白（見 BUBBLE_HOLD_MS 那段）。
  //  用 setTimeout 串而不是 setInterval：顯示與留白的長度不一樣，
  //  固定間隔做不出「說完停一下」的節奏。
  //
  //  只在掩護畫面那段跑。報告出來之後整塊就卸載了，計時器要跟著停，
  //  不然它會在已經卸載的元件上繼續 setState。
  useEffect(() => {
    if (!covering) return undefined;
    const showing = step % 2 === 0;
    const timer = setTimeout(
      () => setStep((n) => (n + 1) % (bubbleCount * 2)),
      showing ? BUBBLE_HOLD_MS : BUBBLE_GAP_MS,
    );
    return () => clearTimeout(timer);
    //  bubbleCount 而不是整個 rp：rp 是每次 render 都重新 find 出來的新物件，
    //  放進相依陣列會讓這個 effect 每次 render 都重掛，節拍就亂了。
    //  真正會影響這段的只有「有幾句」。
  }, [covering, step, bubbleCount]);

  //掩護畫面上的返回。
  //  直接開這個網址進來時（信箱那條連結就是）history 沒有上一頁，
  //  navigate(-1) 會把人送出本站 —— 改導回首頁。作法同 SubHeader。
  const handleCoverBack = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate("/", { replace: true });
  };

  return (
    <main className="pr">
      {/* checking 也要擋掉：它只閃一下，為它畫一條「付款結果」的橫條
          再馬上收掉，比沒有還吵。 */}
      {!covering && !reading && !checking && (
        <SubHeader title="付款結果" fallback="/ask" />
      )}

      <div className="pr__body">
        {state === "waiting" && (
          <>
            <span className="pr__spinner" aria-hidden="true" />
            <h2 className="pr__title">確認付款結果中…</h2>
            <p className="pr__desc">正在跟金流確認，請稍候不要關閉頁面。</p>
          </>
        )}

        {/* 還不知道報告產好了沒。這一段通常只閃一下（一次 API 來回），
            但它必須是中性的 —— 這時候還不能說「正在批你的命」，
            因為很可能那本書早就寫好了，只是我們還沒問到。 */}
        {checking && (
          <>
            <span className="pr__spinner" aria-hidden="true" />
            <h2 className="pr__title">正在翻開你的命書…</h2>
          </>
        )}

        {/* 報告產好了 → 掩護影片退場，直接顯示本文。
            版型是暫時的（見各產品 Report 元件的說明），之後要好好做。 */}
        {state === "paid" && isReportProduct(order) && report && (
          //bookID 給 orderID：它是這份命書的識別，也是記「讀到哪一頁」的 key。
          //  同一台裝置買兩份不會互相覆蓋。
          <rp.Report content={report} bookID={orderID} reviewed={reviewed} />
        )}

        {covering && (
          //—— 報告產出中 ——
          //  不顯示「購買成功」那張卡：買成功只是過程，使用者在意的是
          //  「我的命書呢」。所以直接進入等待，把成功隱含在裡面。
          //
          //  退場條件是「報告產好了」（上面那個分支）。
          //  產不出來就讓它一直播 —— 使用者真正的出口是信箱那條連結，
          //  產好了才會寄，所以停在這裡不會害他錯過什麼。
          <div className="pr__cover">
            {/* 浮在影片上的返回鈕。這一段不掛頂部列（那條深色橫條會壓在他臉上），
                但不能因此讓人出不去 —— 樣子沿用故事頁的那一顆。 */}
            <button
              type="button"
              className="pr__cover-back"
              onClick={handleCoverBack}
              aria-label="返回"
            >
              <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                <path
                  d="M15 5l-7 7 7 7"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {/* 一次只掛一句，key 綁 index —— 每換一句都是全新節點。
                疊在硬體解碼的 <video> 上時，常駐節點改內容在 iOS 真機
                會卡在半透明（intro-gate 踩過那次）。全新節點加
                animation（而不是 transition）才不會。 */}
            {(() => {
              //奇數拍是留白，什麼都不掛
              if (step % 2 === 1) return null;
              const b = rp?.bubbles[step / 2];
              if (!b) return null;
              const text = b.from === "name" ? order?.name : b.text;
              if (!text) return null;
              //暱稱那顆的字級看名字多長，其餘用資料表上的 ratio
              const ratio = b.from === "name" ? nameRatio(text) : b.ratio;
              return (
                <span
                  key={step}
                  className="pr__bubble"
                  style={{
                    top: b.top,
                    left: b.left,
                    width: b.width,
                    fontSize: bubbleFontSize(b.width, ratio),
                    ...(b.aspect ? { "--bubble-aspect": b.aspect } : null),
                  }}
                >
                  {text}
                </span>
              );
            })()}

            <video
              className="pr__cover-video"
              src={videoUrl(rp?.video || "baiye-loading.mp4")}
              //自動播放一定要靜音，不然會被瀏覽器擋掉。
              //  這一段本來就不需要聲音 —— 它是背景，不是一幕戲。
              muted
              autoPlay
              loop
              playsInline
              preload="auto"
              onLoadedMetadata={(e) => {
                e.currentTarget.currentTime = VIDEO_SKIP_S;
              }}
            />

            {/* 疊在影片上的字刻意不掛 transition：iOS 真機上，
                常駐節點＋各自的 transition 疊在硬體解碼的 <video> 上
                會卡在半透明（詳見 intro-gate 踩過的那次）。
                這裡整塊只用一個 animation，而且是 both —— 安全的那一種。 */}
            <div className="pr__cover-text">
              <h2 className="pr__cover-title">{rp?.title}</h2>
              {/* 文案在 REPORT_PRODUCTS 裡，一支產品一組 ——
                  語氣是角色的一部分，寫死在這裡等於兩個角色共用一張嘴。 */}
              <p className="pr__cover-desc">
                {(rp?.desc || []).map((line, i) => (
                  <Fragment key={i}>
                    {i > 0 && <br />}
                    {line}
                  </Fragment>
                ))}
              </p>
              {/* 狀態文字綁 key：換字時整顆重新掛載。
                  疊在播放中的 <video> 上，常駐節點改內容在 iOS 真機
                  會留下半透明殘影（intro-gate 踩過）。 */}
              <p className="pr__cover-state" key={reportStatus || "wait"}>
                {reportStatus === "processing" ? rp?.working : rp?.waiting}
              </p>
            </div>
          </div>
        )}

        {state === "paid" && !isReportProduct(order) && (
          <>
            <span className="pr__icon pr__icon--ok" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="30" height="30">
                <path
                  d="M4 12.5l5 5L20 6.5"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <h2 className="pr__title">購買成功</h2>
            <p className="pr__desc">{purchasedDesc(order)}</p>
            <p className="pr__amount">NT${order?.amount}</p>
          </>
        )}

        {state === "pending" && (
          <>
            <span className="pr__icon pr__icon--wait" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="28" height="28">
                <rect
                  x="3.5"
                  y="5.5"
                  width="17"
                  height="13"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  fill="none"
                />
                <path d="M3.5 10h17" stroke="currentColor" strokeWidth="1.9" />
              </svg>
            </span>
            <h2 className="pr__title">請完成繳費</h2>
            <p className="pr__desc">
              已為你取得繳費資訊（{order?.paymentTypeLabel}）。
              <br />
              完成繳費後就會開始處理。
            </p>
            <dl className="pr__info">
              {order?.paymentInfo?.bankCode && (
                <div>
                  <dt>銀行代碼</dt>
                  <dd>{order.paymentInfo.bankCode}</dd>
                </div>
              )}
              {order?.paymentInfo?.vAccount && (
                <div>
                  <dt>虛擬帳號</dt>
                  <dd className="pr__info-key">{order.paymentInfo.vAccount}</dd>
                </div>
              )}
              {order?.paymentInfo?.paymentNo && (
                <div>
                  <dt>繳費代碼</dt>
                  <dd className="pr__info-key">
                    {order.paymentInfo.paymentNo}
                  </dd>
                </div>
              )}
              {order?.paymentInfo?.expireDate && (
                <div>
                  <dt>繳費期限</dt>
                  <dd>{order.paymentInfo.expireDate}</dd>
                </div>
              )}
              <div>
                <dt>應繳金額</dt>
                <dd>NT${order?.amount}</dd>
              </div>
            </dl>
            <p className="pr__note">
              繳費資訊也保存在「購買紀錄」裡，隨時都查得到。
            </p>
          </>
        )}

        {state === "failed" && (
          <>
            <span className="pr__icon pr__icon--fail" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="28" height="28">
                <path
                  d="M7 7l10 10M17 7L7 17"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <h2 className="pr__title">付款未完成</h2>
            <p className="pr__desc">
              這筆交易沒有完成，款項不會被扣除。
              <br />
              你可以重新選擇方案再試一次。
            </p>
          </>
        )}

        {state === "timeout" && (
          <>
            <span className="pr__icon pr__icon--wait" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="28" height="28">
                <circle
                  cx="12"
                  cy="12"
                  r="9"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  fill="none"
                />
                <path
                  d="M12 7v5.4l3.4 2"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <h2 className="pr__title">處理中</h2>
            <p className="pr__desc">
              金流的確認通知還沒送達，這通常只需要幾分鐘。
              <br />
              稍後到「購買紀錄」就能看到結果，款項不會重複扣除。
            </p>
          </>
        )}

        {state === "notfound" && (
          <>
            <h2 className="pr__title">查無此訂單</h2>
            <p className="pr__desc">請從購買紀錄確認你的交易。</p>
          </>
        )}

        {/* 讀命書的時候不要在底下放「回到問事」「查看購買紀錄」——
            那是結帳流程的收尾，不是一本書該有的東西。 */}
        {/* checking 時也不要畫 —— 那兩顆按鈕只閃一下就被報告蓋掉，
            而且在「正在翻開你的命書」底下出現「回到問事」很矛盾。 */}
        {!reading && !checking && <div className="pr__actions">
          <Link to="/ask" className="pr__btn pr__btn--primary">
            回到問事
          </Link>
          {/* ⚠️ 這裡原本是「查看購買紀錄」指向 /purchase-record ——
              那條 route 不存在（全站掃過，它是唯一一條使用者碰得到的
              死連結），而且前端根本沒有訂單紀錄那一頁：帳號中心只有
              「我的命書」與「優惠券」兩列。
              改導去真的存在的那一頁，標籤也跟著改 —— 把人送到一個
              跟按鈕上寫的不一樣的地方，比死連結更糟。
              真要做訂單紀錄頁的話，後端的 P-8 已經在了。 */}
          <Link to="/my-report" className="pr__btn">
            我的命書
          </Link>
        </div>}
      </div>
    </main>
  );
};

//成功時那一句描述。
//  票券（問卦券、問事券）是加進帳號裡的；報告是之後寄到信箱 ——
//  兩種的下一步完全不一樣，講錯會讓人在帳號裡枯等一份永遠不會出現的券。
//  productName 裡帶著方案名稱（見後端建單時的組法）。
const purchasedDesc = (order) => {
  const name = order?.productName || "";
  //  報告類都是同一句下一步（查表，不要逐個角色寫 if）
  if (REPORT_PRODUCTS.some((p) => name.includes(p.match))) {
    return "報告正在產出，完成後會寄到你填的信箱。";
  }
  return `${name} 已加入你的帳號，可以開始問事了。`;
};

export default PurchaseResultPage;
